import moment from 'moment';

import { formatDate } from 'shared/helpers/date-time';

/**
 * Mask a reviewer's name for display, e.g. ("John", "Evans") -> "J***e".
 * First initial of the first name (uppercase) + a fixed "***" + last initial
 * of the last name (lowercase).
 */
export function maskReviewerName(firstName?: string | null, lastName?: string | null): string {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();

  if (!first && !last) return 'Anonymous';

  const firstInitial = first ? first.charAt(0).toUpperCase() : '';
  const lastInitial = last ? last.charAt(0).toLowerCase() : '';

  return `${firstInitial}***${lastInitial}`;
}

/** Single uppercase initial for the avatar badge. */
export function reviewerInitial(firstName?: string | null, lastName?: string | null): string {
  // An empty-string first name should fall through to the last name (?? wouldn't).
  const first = (firstName ?? '').trim();
  const source = first !== '' ? first : (lastName ?? '').trim();
  return source.charAt(0).toUpperCase() || '?';
}

/** Format an ISO timestamp as e.g. "May 8, 2026". */
export function formatReviewDate(date?: string | null): string {
  if (!date || !moment(date).isValid()) return '';
  return formatDate(date, 'MMMM D, YYYY');
}

export const MAX_TOP_EFFECTS = 3;

/** Parse the `effectsCounts` JSON map into the top N effects by mention count. */
export function topEffects(effectsCounts: unknown, max = MAX_TOP_EFFECTS): Array<{ effect: string; count: number }> {
  if (!effectsCounts || typeof effectsCounts !== 'object') return [];
  return Object.entries(effectsCounts as Record<string, unknown>)
    .map(([effect, count]) => ({ effect, count: Number(count) }))
    .filter(({ count }) => Number.isFinite(count) && count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, max);
}
